/**
 * Parameter form for one indicator instance.
 *
 * Shared by the chip strip and the on-chart legend so the two cannot drift
 * into offering different controls for the same indicator. Values are clamped
 * to the catalogue's declared range here, so the engine never has to reject a
 * value this UI itself offered.
 */
import type { IndicatorDef } from "@tt/shared";

export function IndicatorSettings({
  def, params, onChange,
}: {
  def: IndicatorDef;
  params: Record<string, unknown>;
  onChange: (patch: Record<string, unknown>) => void;
}) {
  return (
    <div className="settings-body">
      <div className="settings-title">{def.name}</div>
      {def.params.length === 0 && <div className="faint small">No settings.</div>}
      {def.params.map((p) => (
        <ParamControl
          key={p.name}
          param={p}
          value={params[p.name] ?? p.default}
          onChange={(v) => onChange({ [p.name]: v })}
        />
      ))}
      {def.notes.map((n) => (
        <div className="small" style={{ color: "var(--tone-warn)", marginTop: 6 }} key={n}>
          {n}
        </div>
      ))}
      <div className="faint small" style={{ marginTop: 6 }}>{def.description}</div>
    </div>
  );
}

const SOURCES = ["close", "open", "high", "low", "hl2", "hlc3", "ohlc4", "hlcc4", "volume"];

export function ParamControl({
  param, value, onChange,
}: {
  param: IndicatorDef["params"][number];
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  if (param.kind === "bool") {
    return (
      <label>
        {param.label}
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
        />
      </label>
    );
  }
  if (param.kind === "source") {
    return (
      <label>
        {param.label}
        <select
          className="select"
          value={String(value)}
          onChange={(e) => onChange(e.target.value)}
        >
          {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </label>
    );
  }
  if (param.options && param.options.length) {
    return (
      <label>
        {param.label}
        <select
          className="select"
          value={String(value)}
          onChange={(e) => onChange(e.target.value)}
        >
          {param.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </label>
    );
  }
  return (
    <label>
      {param.label}
      <input
        className="input"
        type="number"
        value={value === null || value === undefined ? "" : String(value)}
        min={param.minval ?? undefined}
        max={param.maxval ?? undefined}
        step={param.step ?? (param.kind === "int" ? 1 : "any")}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "") return;
          const n = Number(raw);
          if (!Number.isFinite(n)) return;
          const lo = param.minval ?? -Infinity;
          const hi = param.maxval ?? Infinity;
          const clamped = Math.min(hi, Math.max(lo, n));
          onChange(param.kind === "int" ? Math.round(clamped) : clamped);
        }}
      />
    </label>
  );
}
